import { BetSlipGroupViewModel, BetSlipSelectionViewModel } from '../sportsbook/sportsbook.models';
import {
  BetSlipBonusHint,
  BetSlipCombiBonusTier,
  BetSlipMode,
  BetSlipSummaryOptions,
  BetSlipSummaryViewModel,
  BetSlipTicketPayload,
  BetSlipTicketType
} from './betslip-ticket.models';

const DEFAULT_MIN_STAKE = 1;
const DEFAULT_MAX_STAKE = 1000;
const DEFAULT_TAX_RATE = 0;
const DEFAULT_MAX_ODDS = 100000;
const DEFAULT_MAX_PROFIT = 100000;
const DEFAULT_MIN_COMBINATION_ODDS = 1.3;
const TICKET_ORIGIN_WEB = 8;

enum TicketType {
  Single = 1,
  Combi = 2,
  System = 3,
  Multiway = 4
}

export function buildBetSlipSummary(
  groups: readonly BetSlipGroupViewModel[],
  options: BetSlipSummaryOptions
): BetSlipSummaryViewModel {
  groups = uniqueBetSlipGroups(groups);
  const selectionCount = groups.reduce((total, group) => total + group.selections.length, 0);
  const matchCount = groups.length;
  const canUseSystem = matchCount >= 3;
  const effectiveMode: BetSlipMode = options.mode === 'system' && canUseSystem ? 'system' : 'combi';
  const systemY = matchCount;
  const systemX = effectiveMode === 'system' ? clampSystemX(options.systemX, systemY) : systemY;
  const systemRows = effectiveMode === 'system' ? binom(systemY, systemX) : 0;
  const wayCount = matchCount ? groups.reduce((total, group) => total * group.selections.length, 1) : 0;
  const minStake = options.minStake ?? DEFAULT_MIN_STAKE;
  const maxStake = options.maxStake ?? DEFAULT_MAX_STAKE;
  const maxAllowedStake = Math.min(maxStake, options.balance ?? maxStake);
  const taxRate = options.taxRate ?? DEFAULT_TAX_RATE;
  const stake = roundMoney(Math.max(options.stake, 0));
  const tax = roundMoney(calculateTax(stake, taxRate));
  const netStake = roundMoney(Math.max(stake - tax, 0));
  const minCombinationOdds = options.minCombinationOdds ?? DEFAULT_MIN_COMBINATION_ODDS;
  const minCombination = matchCount
    ? Math.max(...groups.map(group => Math.max(group.minCombination, 1)))
    : 1;
  const qualifiedMatchCount = groups
    .filter(group => group.selections.some(selection => selection.odds >= minCombinationOdds))
    .length;
  const minCombinationReached = selectionCount > 0 && (
    minCombination <= 1 ||
    matchCount >= (minCombination * 2 - 1) ||
    (qualifiedMatchCount >= minCombination && (effectiveMode !== 'system' || systemX >= minCombination))
  );
  const invalidSelectionCount = groups.reduce(
    (total, group) => total + group.selections.filter(selection => !selection.active).length,
    0
  );
  const hasOutrightConflict = new Set(groups.filter(group => group.isOutright).map(group => group.matchId)).size > 1;
  const hasH2HConflict = hasConflictingH2HGroups(groups);
  const oddsResult = calculateOddsResult(groups, {
    mode: effectiveMode,
    systemX,
    netStake,
    maxOdds: options.maxOdds ?? DEFAULT_MAX_ODDS,
    maxProfit: calculateTaxedMaxProfit(options.maxProfit ?? DEFAULT_MAX_PROFIT, taxRate),
    minCombinationOdds,
    combiBonus: options.combiBonus ?? []
  });
  const requiresLogin = selectionCount > 0 && options.balance === null;
  const stakeTooLow = selectionCount > 0 && stake < minStake;
  const stakeTooHigh = selectionCount > 0 && stake > maxAllowedStake;
  const hasSufficientFunds = options.balance === null || stake <= options.balance;
  const blockingState = getBlockingState({
    selectionCount,
    requiresLogin,
    stakeTooLow,
    stakeTooHigh,
    minStake,
    maxAllowedStake,
    minCombination,
    minCombinationOdds,
    qualifiedMatchCount,
    minCombinationReached,
    invalidSelectionCount,
    hasOutrightConflict,
    hasH2HConflict
  });
  const canSubmit = selectionCount > 0
    && !requiresLogin
    && !stakeTooLow
    && !stakeTooHigh
    && hasSufficientFunds
    && minCombinationReached
    && invalidSelectionCount === 0
    && !hasOutrightConflict
    && !hasH2HConflict;

  return {
    mode: options.mode,
    effectiveMode,
    ticketType: resolveTicketType(groups, effectiveMode),
    ticketTypeKey: resolveTicketTypeKey(groups, effectiveMode),
    selectionCount,
    matchCount,
    wayCount,
    systemX,
    systemY,
    systemRows,
    canUseSystem,
    canDecreaseSystemX: effectiveMode === 'system' && systemX > 2,
    canIncreaseSystemX: effectiveMode === 'system' && systemX < Math.max(systemY - 1, 2),
    stake,
    minStake,
    maxStake,
    maxAllowedStake,
    stakeTooLow,
    stakeTooHigh,
    taxRate,
    tax,
    netStake,
    totalOdds: oddsResult.totalOdds,
    bonusPercent: oddsResult.bonusPercent,
    maxWin: oddsResult.maxWin,
    minCombination,
    minCombinationOdds,
    qualifiedMatchCount,
    minCombinationReached,
    invalidSelectionCount,
    hasOutrightConflict,
    hasH2HConflict,
    requiresLogin,
    hasSufficientFunds,
    canSubmit,
    primaryActionEnabled: selectionCount > 0 && (canSubmit || requiresLogin),
    primaryActionKey: requiresLogin ? 'betslip.loginToPlace' : 'betslip.place',
    blockingMessageKey: blockingState.key,
    blockingMessageParams: blockingState.params
  };
}

export function buildBetSlipBonusHint(
  summary: BetSlipSummaryViewModel,
  tiers: readonly BetSlipCombiBonusTier[]
): BetSlipBonusHint | null {
  if (summary.selectionCount < 2 || summary.ticketType !== 'combi' || summary.invalidSelectionCount > 0
    || summary.hasOutrightConflict || summary.hasH2HConflict) {
    return null;
  }

  const nextTier = tiers
    .filter(tier => tier.combi > summary.qualifiedMatchCount && tier.bonus > summary.bonusPercent)
    .sort((left, right) => left.combi - right.combi)[0];

  return nextTier ? {
    missingMatches: nextTier.combi - summary.qualifiedMatchCount,
    minOdds: summary.minCombinationOdds,
    bonusPercent: nextTier.bonus
  } : null;
}

export function buildBetSlipTicketPayload(
  groups: readonly BetSlipGroupViewModel[],
  summary: BetSlipSummaryViewModel,
  lb2so: number
): BetSlipTicketPayload {
  return {
    Bets: uniqueBetSlipGroups(groups).flatMap(group => group.selections.map(selection => ({
      LB_OddID: selection.oddId,
      LB_BetTypeID: selection.betTypeId,
      LB_MatchID: group.matchId,
      Bank: false,
      BetTypeKey: selection.betTypeKey,
      MatchStartDate: group.matchStartDate,
      SpecialOddValue: selection.specialOddsValue,
      MatchIdentifier: group.matchIdentifier,
      IsLive: group.isLive,
      MatchNumber: group.matchNumber,
      Sport: group.sportId,
      Active: group.active && selection.active,
      OddTypeKey: selection.oddTypeKey,
      OddFieldType: selection.oddFieldType,
      Odd: selection.odds,
      Competitors: group.competitors
    }))),
    SystemX: summary.systemX,
    SystemY: summary.systemY,
    Stake: summary.netStake,
    Tax: summary.tax,
    MaxOdd: summary.totalOdds,
    MaxProfit: summary.maxWin,
    Type: ticketTypeToApiType(summary.ticketType),
    WayCount: summary.wayCount,
    SystemRows: summary.systemRows,
    Bonus: summary.bonusPercent,
    LB2SO: lb2so,
    TicketOrigin: TICKET_ORIGIN_WEB
  };
}

/** Keep calculations and the API payload consistent even if duplicate selections reach this layer. */
function uniqueBetSlipGroups(groups: readonly BetSlipGroupViewModel[]): BetSlipGroupViewModel[] {
  const uniqueGroups = new Map<number, BetSlipGroupViewModel>();
  const oddIds = new Set<number>();

  for (const group of groups) {
    const selections = group.selections.filter(selection => {
      if (oddIds.has(selection.oddId)) {
        return false;
      }
      oddIds.add(selection.oddId);
      return true;
    });
    if (!selections.length) {
      continue;
    }
    const existing = uniqueGroups.get(group.matchId);
    uniqueGroups.set(group.matchId, existing
      ? { ...existing, selections: [...existing.selections, ...selections] }
      : { ...group, selections });
  }

  return Array.from(uniqueGroups.values());
}

export function clampSystemX(systemX: number, systemY: number): number {
  if (systemY < 3) {
    return systemY;
  }

  return Math.min(Math.max(systemX, 2), systemY - 1);
}

function calculateOddsResult(
  groups: readonly BetSlipGroupViewModel[],
  options: {
    readonly mode: BetSlipMode;
    readonly systemX: number;
    readonly netStake: number;
    readonly maxOdds: number;
    readonly maxProfit: number;
    readonly minCombinationOdds: number;
    readonly combiBonus: readonly { readonly combi: number; readonly bonus: number }[];
  }
): { readonly totalOdds: number; readonly bonusPercent: number; readonly maxWin: number } {
  if (!groups.length) {
    return { totalOdds: 0, bonusPercent: 0, maxWin: 0 };
  }

  if (options.mode === 'system') {
    return calculateSystemOddsResult(groups, options);
  }

  const highestSelections = groups.map(group => maxBy(group.selections, selection => selection.odds));
  const totalOdds = Math.min(
    highestSelections.reduce((product, selection) => product * selection.odds, 1),
    options.maxOdds
  );
  const bonusPercent = calculateBonusPercent(highestSelections, undefined, options);
  const wayCount = groups.reduce((total, group) => total * group.selections.length, 1);
  const maxWin = Math.min(
    options.netStake / wayCount * totalOdds * (1 + bonusPercent / 100),
    options.maxProfit
  );

  return {
    totalOdds: roundOdds(totalOdds),
    bonusPercent,
    maxWin: roundMoney(maxWin)
  };
}

function calculateSystemOddsResult(
  groups: readonly BetSlipGroupViewModel[],
  options: {
    readonly systemX: number;
    readonly netStake: number;
    readonly maxOdds: number;
    readonly maxProfit: number;
    readonly minCombinationOdds: number;
    readonly combiBonus: readonly { readonly combi: number; readonly bonus: number }[];
  }
): { readonly totalOdds: number; readonly bonusPercent: number; readonly maxWin: number } {
  const ways = buildWays(groups.map(group => group.selections));
  const wayCount = Math.max(ways.length, 1);
  let totalOdds = 0;
  let maxWin = 0;
  let bonusPercent = 0;

  for (const way of ways) {
    const combinations = kCombinations(way, options.systemX);
    if (!combinations.length) {
      continue;
    }

    const wayOdds = combinations
      .reduce((sum, combination) => sum + combination.reduce((product, selection) => product * selection.odds, 1), 0) / combinations.length;
    const wayBonus = calculateBonusPercent(way, options.systemX, options);

    bonusPercent = Math.max(bonusPercent, wayBonus);
    totalOdds += wayOdds;
    maxWin += options.netStake / wayCount * wayOdds * (1 + wayBonus / 100);
  }

  return {
    totalOdds: roundOdds(Math.min(totalOdds, options.maxOdds)),
    bonusPercent,
    maxWin: roundMoney(Math.min(maxWin, options.maxProfit))
  };
}

function calculateBonusPercent(
  selections: readonly BetSlipSelectionViewModel[],
  systemX: number | undefined,
  options: {
    readonly minCombinationOdds: number;
    readonly combiBonus: readonly { readonly combi: number; readonly bonus: number }[];
  }
): number {
  const eligibleSelections = selections.filter(selection => selection.odds >= options.minCombinationOdds);
  const bonusCount = systemX === undefined ? eligibleSelections.length : Math.min(eligibleSelections.length, systemX);
  return options.combiBonus.find(tier => tier.combi === bonusCount)?.bonus ?? 0;
}

function resolveTicketType(groups: readonly BetSlipGroupViewModel[], mode: BetSlipMode): BetSlipTicketType {
  const wayCount = groups.length ? groups.reduce((total, group) => total * group.selections.length, 1) : 0;

  if (groups.length === 1 && wayCount === 1) {
    return 'single';
  }

  if (wayCount > 1 && mode === 'system') {
    return 'multiway-system';
  }

  if (wayCount > 1) {
    return 'multiway';
  }

  return mode === 'system' ? 'system' : 'combi';
}

function resolveTicketTypeKey(groups: readonly BetSlipGroupViewModel[], mode: BetSlipMode): string {
  return `betslip.ticketTypes.${resolveTicketType(groups, mode)}`;
}

function ticketTypeToApiType(ticketType: BetSlipTicketType): number {
  switch (ticketType) {
    case 'single':
      return TicketType.Single;
    case 'combi':
      return TicketType.Combi;
    case 'system':
      return TicketType.System;
    case 'multiway':
    case 'multiway-system':
      return TicketType.Multiway;
  }
}

function getBlockingState(context: {
  readonly selectionCount: number;
  readonly requiresLogin: boolean;
  readonly stakeTooLow: boolean;
  readonly stakeTooHigh: boolean;
  readonly minStake: number;
  readonly maxAllowedStake: number;
  readonly minCombination: number;
  readonly minCombinationOdds: number;
  readonly qualifiedMatchCount: number;
  readonly minCombinationReached: boolean;
  readonly invalidSelectionCount: number;
  readonly hasOutrightConflict: boolean;
  readonly hasH2HConflict: boolean;
}): { readonly key: string | null; readonly params?: Record<string, string | number> } {
  if (!context.selectionCount) {
    return { key: 'betslip.messages.addSelection' };
  }

  if (context.requiresLogin) {
    return { key: 'betslip.messages.loginRequired' };
  }

  if (context.stakeTooLow) {
    return { key: 'betslip.messages.minStake', params: { min: context.minStake } };
  }

  if (context.stakeTooHigh) {
    return { key: 'betslip.messages.maxStake', params: { max: context.maxAllowedStake } };
  }

  if (context.invalidSelectionCount) {
    return { key: 'betslip.messages.invalidSelections' };
  }

  if (context.hasOutrightConflict) {
    return { key: 'betslip.messages.outrightConflict' };
  }

  if (context.hasH2HConflict) {
    return { key: 'betslip.messages.h2hConflict' };
  }

  if (!context.minCombinationReached) {
    return {
      key: 'betslip.messages.minCombination',
      params: {
        count: context.minCombination,
        minOdds: context.minCombinationOdds,
        current: context.qualifiedMatchCount
      }
    };
  }

  return { key: null };
}

function hasConflictingH2HGroups(groups: readonly BetSlipGroupViewModel[]): boolean {
  const h2hGroups = groups.filter(group => group.isH2H);
  if (h2hGroups.length <= 1) {
    return false;
  }

  return new Set(h2hGroups.map(group => group.homeTeamBrUniqueId)).size !== h2hGroups.length
    || new Set(h2hGroups.map(group => group.awayTeamBrUniqueId)).size !== h2hGroups.length;
}

function buildWays(selectionGroups: readonly (readonly BetSlipSelectionViewModel[])[]): BetSlipSelectionViewModel[][] {
  return selectionGroups.reduce<BetSlipSelectionViewModel[][]>(
    (ways, selections) => ways.flatMap(way => selections.map(selection => [...way, selection])),
    [[]]
  );
}

function kCombinations<T>(values: readonly T[], size: number): T[][] {
  if (size === 0) {
    return [[]];
  }

  if (size > values.length) {
    return [];
  }

  if (size === values.length) {
    return [[...values]];
  }

  return values.flatMap((value, index) => (
    kCombinations(values.slice(index + 1), size - 1).map(combination => [value, ...combination])
  ));
}

function maxBy<T>(values: readonly T[], selector: (value: T) => number): T {
  return values.reduce((best, value) => selector(value) > selector(best) ? value : best);
}

function calculateTax(stake: number, taxRate: number): number {
  if (taxRate < 0) {
    return stake - (stake / (((taxRate * -1) / 100) + 1));
  }

  return stake * (taxRate / 100);
}

function calculateTaxedMaxProfit(maxProfit: number, taxRate: number): number {
  if (taxRate < 0) {
    return maxProfit / (((taxRate * -1) / 100) + 1);
  }

  return maxProfit - (maxProfit * (taxRate / 100));
}

function binom(n: number, k: number): number {
  let coeff = 1;

  for (let i = n - k + 1; i <= n; i++) {
    coeff *= i;
  }

  for (let i = 1; i <= k; i++) {
    coeff /= i;
  }

  return coeff;
}

function roundMoney(value: number): number {
  return Math.round(value * 100) / 100;
}

function roundOdds(value: number): number {
  return Math.round(value * 10000) / 10000;
}
